import { useState } from "react"

const BankAccountModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    expiryDate: "",
    cvc: "",
  })

  const [errors, setErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    let formattedValue = value

    // Format account number with spaces
    if (name === "accountNumber") {
      formattedValue = value.replace(/\s/g, "").replace(/(.{4})/g, "$1 ").trim()
      if (formattedValue.length > 19) return // Limit to 16 digits + 3 spaces
    }

    // Format expiry date as MM/YYYY
    if (name === "expiryDate") {
      formattedValue = value.replace(/\D/g, "")
      if (formattedValue.length >= 2) {
        formattedValue = `${formattedValue.slice(0, 2)}/${formattedValue.slice(2, 6)}`
      }
      if (formattedValue.length > 7) return
    }

    // Limit CVC to 3 digits
    if (name === "cvc") {
      formattedValue = value.replace(/\D/g, "").slice(0, 3)
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = "Account holder name is required"
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = "Account number is required"
    } else if (formData.accountNumber.replace(/\s/g, "").length !== 16) {
      newErrors.accountNumber = "Account number must be 16 digits"
    }

    if (!formData.bankName.trim()) {
      newErrors.bankName = "Bank name is required"
    }

    if (!formData.expiryDate.trim()) {
      newErrors.expiryDate = "Expiry date is required"
    } else if (!/^\d{2}\/\d{4}$/.test(formData.expiryDate)) {
      newErrors.expiryDate = "Invalid expiry date format"
    }

    if (!formData.cvc.trim()) {
      newErrors.cvc = "CVC is required"
    } else if (formData.cvc.length !== 3) {
      newErrors.cvc = "CVC must be 3 digits"
    }

    return newErrors
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const newErrors = validateForm()

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Save the bank account data
    onSave({
      bankName: formData.bankName.toUpperCase(),
      cardNumber: formData.accountNumber,
      holderName: formData.accountHolderName.toUpperCase(),
      expiryDate: formData.expiryDate.slice(0, 5), // MM/YY format
    })

    // Reset form and close modal
    setFormData({
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      expiryDate: "",
      cvc: "",
    })
    setErrors({})
    onClose()
  }

  const handleClose = () => {
    setFormData({
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      expiryDate: "",
      cvc: "",
    })
    setErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: "20px",
          padding: "0",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "32px 40px 0 40px",
            borderBottom: "1px solid #E5E7EB",
            paddingBottom: "24px",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h2
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "28px",
                fontWeight: 600,
                color: "#303E4E",
                margin: "0",
              }}
            >
              Add Bank Account
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "8px",
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#F9FAFB"
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent"
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tab Headers */}
        <div
          style={{
            display: "flex",
            margin: "0 40px 32px 40px",
          }}
        >
          <div
            style={{
              flex: 1,
              padding: "12px 24px",
              background: "linear-gradient(135deg, #D1D5DB 0%, #E5E7EB 100%)",
              color: "#6B7280",
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "14px",
              fontWeight: 600,
              textAlign: "center",
              borderTopLeftRadius: "8px",
              borderBottomLeftRadius: "8px",
              letterSpacing: "1px",
            }}
          >
            CREDIT CARD
          </div>
          <div
            style={{
              flex: 1,
              padding: "12px 24px",
              background: "linear-gradient(135deg, #D1716C 0%, #E5A19C 100%)",
              color: "white",
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "14px",
              fontWeight: 600,
              textAlign: "center",
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
              letterSpacing: "1px",
            }}
          >
            BANK NAME
          </div>
        </div>

        {/* Form Content */}
        <div style={{ padding: "0 40px 40px 40px" }}>
          <div
            style={{
              background: "#FFFFFF",
              border: "2px solid #E5E7EB",
              borderRadius: "16px",
              padding: "32px",
            }}
          >
            <h3
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "24px",
                fontWeight: 600,
                color: "#303E4E",
                margin: "0 0 24px 0",
              }}
            >
              Bank Account
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Account Holder Name */}
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#303E4E",
                    display: "block",
                    marginBottom: "8px",
                  }}
                >
                  Account Holder Name <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleInputChange}
                  placeholder="Enter Card Holder Name"
                  style={{
                    width: "100%",
                    height: "48px",
                    padding: "0 16px",
                    border: `1.5px solid ${errors.accountHolderName ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "8px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    color: "#303E4E",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) => {
                    if (!errors.accountHolderName) {
                      e.target.style.borderColor = "#8548A8"
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.accountHolderName) {
                      e.target.style.borderColor = "#E5E7EB"
                    }
                  }}
                />
                {errors.accountHolderName && (
                  <div
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      color: "#EF4444",
                      marginTop: "4px",
                    }}
                  >
                    {errors.accountHolderName}
                  </div>
                )}
              </div>

              {/* Account Number */}
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#303E4E",
                    display: "block",
                    marginBottom: "8px",
                  }}
                >
                  Account number <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleInputChange}
                  placeholder="1234 5678 9012 3456"
                  style={{
                    width: "100%",
                    height: "48px",
                    padding: "0 16px",
                    border: `1.5px solid ${errors.accountNumber ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "8px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    color: "#303E4E",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) => {
                    if (!errors.accountNumber) {
                      e.target.style.borderColor = "#8548A8"
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.accountNumber) {
                      e.target.style.borderColor = "#E5E7EB"
                    }
                  }}
                />
                {errors.accountNumber && (
                  <div
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      color: "#EF4444",
                      marginTop: "4px",
                    }}
                  >
                    {errors.accountNumber}
                  </div>
                )}
              </div>

              {/* Bank Name */}
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#303E4E",
                    display: "block",
                    marginBottom: "8px",
                  }}
                >
                  Bank name <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder="1234 5678 9012 3456"
                  style={{
                    width: "100%",
                    height: "48px",
                    padding: "0 16px",
                    border: `1.5px solid ${errors.bankName ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "8px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    color: "#303E4E",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) => {
                    if (!errors.bankName) {
                      e.target.style.borderColor = "#8548A8"
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.bankName) {
                      e.target.style.borderColor = "#E5E7EB"
                    }
                  }}
                />
                {errors.bankName && (
                  <div
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      color: "#EF4444",
                      marginTop: "4px",
                    }}
                  >
                    {errors.bankName}
                  </div>
                )}
              </div>

              {/* Expiry Date and CVC */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginBottom: "32px",
                }}
              >
                {/* Expiry Date */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#303E4E",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Expiry Date <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    placeholder="mm/yyyy"
                    style={{
                      width: "100%",
                      height: "48px",
                      padding: "0 16px",
                      border: `1.5px solid ${errors.expiryDate ? "#EF4444" : "#E5E7EB"}`,
                      borderRadius: "8px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#303E4E",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      if (!errors.expiryDate) {
                        e.target.style.borderColor = "#8548A8"
                      }
                    }}
                    onBlur={(e) => {
                      if (!errors.expiryDate) {
                        e.target.style.borderColor = "#E5E7EB"
                      }
                    }}
                  />
                  {errors.expiryDate && (
                    <div
                      style={{
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontSize: "14px",
                        color: "#EF4444",
                        marginTop: "4px",
                      }}
                    >
                      {errors.expiryDate}
                    </div>
                  )}
                </div>

                {/* CVC */}
                <div>
                  <label
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#303E4E",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    CVC <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="cvc"
                    value={formData.cvc}
                    onChange={handleInputChange}
                    placeholder="123"
                    style={{
                      width: "100%",
                      height: "48px",
                      padding: "0 16px",
                      border: `1.5px solid ${errors.cvc ? "#EF4444" : "#E5E7EB"}`,
                      borderRadius: "8px",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "16px",
                      color: "#303E4E",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => {
                      if (!errors.cvc) {
                        e.target.style.borderColor = "#8548A8"
                      }
                    }}
                    onBlur={(e) => {
                      if (!errors.cvc) {
                        e.target.style.borderColor = "#E5E7EB"
                      }
                    }}
                  />
                  {errors.cvc && (
                    <div
                      style={{
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontSize: "14px",
                        color: "#EF4444",
                        marginTop: "4px",
                      }}
                    >
                      {errors.cvc}
                    </div>
                  )}
                </div>
              </div>

              {/* Add Card Button */}
              <button
                type="submit"
                style={{
                  width: "100%",
                  height: "56px",
                  background: "#8548A8",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "14px",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  marginBottom: "16px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#7A4397"
                  e.target.style.transform = "translateY(-1px)"
                  e.target.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)"
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#8548A8"
                  e.target.style.transform = "translateY(0)"
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"
                }}
              >
                Add Card
              </button>

              {/* Security Notice */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#6B7280">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    color: "#6B7280",
                  }}
                >
                  Your payment info will be stored securely
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BankAccountModal